import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney, fmtPct, currentMonth } from "@/lib/format";
import { PageHeader } from "@/components/common/Page";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { FormDialog } from "@/components/common/FormDialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useProducts, useCustomers, productOptions, customerOptions, EXPENSE_CATEGORIES } from "@/lib/lookups";

export default function Budget() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const month = currentMonth();
  const { data } = useQuery({ queryKey: ["budget-status", month], queryFn: async () => (await api.get(`/finance/budget-status?period=${month}`)).data });
  const { data: products } = useProducts();
  const { data: customers } = useCustomers();

  const save = useMutation({
    mutationFn: async (p) => api.post("/budgets", p),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Budget saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({ mutationFn: async (id) => api.delete(`/budgets/${id}`), onSuccess: () => { qc.invalidateQueries(); toast.success("Budget removed"); } });

  const fields = [
    { name: "period", label: "Period (YYYY-MM or YYYY)", default: month, placeholder: "2026-06" },
    { name: "scope", label: "Scope", type: "select", options: [
      { value: "company", label: "Company" }, { value: "product", label: "Product" },
      { value: "customer", label: "Customer" }, { value: "category", label: "Category" }], default: "company" },
    { name: "scope_id", label: "Product / Customer", type: "select", options: [
      ...productOptions(products), ...customerOptions(customers, false)] },
    { name: "category", label: "Category (if category scope)", type: "select", options: [{ value: "__none__", label: "— None —" }, ...EXPENSE_CATEGORIES] },
    { name: "amount", label: "Budget Amount", type: "number", step: "0.01" },
    { name: "committed", label: "Committed", type: "number", step: "0.01", default: 0 },
    { name: "notes", label: "Notes", type: "textarea", full: true },
  ];

  const columns = [
    { key: "period", header: "Period" },
    { key: "scope", header: "Scope", render: (r) => <span className="capitalize">{r.scope}</span> },
    { key: "category", header: "Category", render: (r) => r.category || "—" },
    { key: "budget", header: "Budget", align: "right", mono: true, render: (r) => fmtMoney(r.budget) },
    { key: "used", header: "Actual + Committed", align: "right", mono: true, render: (r) => fmtMoney(r.used) },
    { key: "variance", header: "Variance", align: "right", mono: true, render: (r) => <span className={r.variance >= 0 ? "text-emerald-400" : "text-rose-400"}>{fmtMoney(r.variance)}</span> },
    { key: "variance_pct", header: "Used %", align: "right", mono: true, render: (r) => fmtPct(r.variance_pct) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "actions", header: "", align: "right", render: (r) => (
      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-rose-400" onClick={() => del.mutate(r.id)} data-testid={`delete-budget-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
    ) },
  ];

  return (
    <div className="ns-fade">
      <PageHeader title="Budget" subtitle="Budget vs actual with variance tracking" testId="budget-header">
        <Button onClick={() => setOpen(true)} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500" data-testid="add-budget-button">
          <Plus className="mr-1.5 h-4 w-4" /> Add Budget
        </Button>
      </PageHeader>
      <DataTable columns={columns} rows={data?.budgets} testId="budget-table" empty={{ title: "No budgets set", message: "Create a monthly, product, customer or category budget to track spend against target." }} />
      <FormDialog open={open} onOpenChange={setOpen} title="Add Budget" fields={fields} onSubmit={(p) => save.mutateAsync(p).then(() => true).catch(() => false)} testId="budget-form" />
    </div>
  );
}
